import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Modal from "@cloudscape-design/components/modal"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import { useQueryClient } from "@tanstack/react-query"
import { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import type { ParsedFormResult } from "@/generated/sdk"
import { useMyAccess } from "@/hooks/queries/useMyAccess"
import type { DTAEditorHandle } from "../../../components/DTAEditor/DTAEditor"
import type { DTADetails } from "../../../services/dtaApi"
import type { DTADocumentContent, DTAVersion } from "../../../types/dta"
import CommentsRail from "../../../components/DTAEditor/comments/CommentsRail"
import {
  DtaCommentsProvider,
  useDtaComments,
} from "../../../components/DTAEditor/comments/DtaCommentsContext"
import DTAEditor from "../../../components/DTAEditor/DTAEditor"
import DomainSelectionStep from "../../../components/DTAUploadModal/DomainSelectionStep"
import Breadcrumb from "../../../components/Navigation/Breadcrumb"
import { getDTADisplayName } from "../../../components/shared/dtaDisplay"
import SharedTabs from "../../../components/shared/SharedTabs"
import { useAuth } from "../../../contexts/AuthContext"
import { useNotifications } from "../../../contexts/NotificationContext"
import {
  dtaKeys,
  useUpdateDta,
  useUploadAndParseEdc,
  useUploadAndParseSdtm,
  useUploadDocument,
} from "../../../hooks/queries/useDtas"
import { useInlineDiff } from "../../../hooks/useInlineDiff"
import { dtaApiService } from "../../../services/dtaApi"
import { isTableNameSaveError, parseApiErrorMessage } from "../../../utils/apiErrors"
import { validateDateFormats } from "../../../utils/dateFormatValidation"
import AmendModal from "./AmendModal"
import AuditTrailPanel from "./AuditTrailPanel"
import CollaborationPanel from "./CollaborationPanel"
import DataStreamActions from "./DataStreamActions"
import DataStreamContentActions from "./DataStreamContentActions"
import DataStreamHeader from "./DataStreamHeader"
import DataStreamSkeleton from "./DataStreamSkeleton"
import { handleEdcReuploadReplace } from "./edcReupload"
import { canEditDta } from "./editability"
import EditorTabMessages from "./EditorTabMessages"
import OverviewPanel from "./OverviewPanel"
import { isSpecDerivedSource, selectReuploadForm } from "./reuploadForms"
import SignModal from "./SignModal"
import { stageGuidanceText } from "./stageGuidance"
import { DTA_TAB_LABELS } from "./tabLabels"
import { useAmendment } from "./useAmendment"
import { useDataStreamDetails } from "./useDataStreamDetails"
import { useSigningWorkflow } from "./useSigningWorkflow"
import ValidationErrorsAlert from "./ValidationErrorsAlert"
import "./DataStreamDetails.css"

/**
 * Register the page tab switcher with the comments context so the rail can open the editor tab
 * before scrolling to an anchor in it. Renders nothing.
 */
const PageTabBridge = ({ onSwitchTab }: { onSwitchTab: (tabId: string) => void }) => {
  const comments = useDtaComments()
  const setPageTabSwitcher = comments?.setPageTabSwitcher
  useEffect(() => {
    setPageTabSwitcher?.(onSwitchTab)
  }, [setPageTabSwitcher, onSwitchTab])
  return null
}

const hasTableName = (documentContent: unknown): boolean => {
  if (!documentContent || typeof documentContent !== "object") return false
  const rules = (documentContent as Record<string, unknown>).data_quality_rules
  if (!rules || typeof rules !== "object") return false
  const table = (rules as Record<string, unknown>).table
  if (!table || typeof table !== "object") return false
  const name = (table as Record<string, unknown>).name
  return typeof name === "string" && name.trim().length > 0
}

/**
 * DTA details page: header, tab panels, and modals.
 */
const DataStreamDetails = () => {
  const { dtaId, version: urlVersion } = useParams<{ dtaId: string; version: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { showToast } = useNotifications()
  const [searchParams, setSearchParams] = useSearchParams()

  const { isInternal: isDAE, isExternal: isVendor } = useMyAccess()

  const [savingDocument, setSavingDocument] = useState(false)
  const [documentEditError, setDocumentEditError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<
    Array<{ field: string; tab: string; message: string }>
  >([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [recoveredDraft, setRecoveredDraft] = useState<Record<string, unknown> | null>(null)
  const [edcReuploadForms, setEdcReuploadForms] = useState<ParsedFormResult[] | null>(null)
  const [edcReuploadSelected, setEdcReuploadSelected] = useState<string>("")
  const [edcReuploadLoading, setEdcReuploadLoading] = useState(false)
  const editorRef = useRef<DTAEditorHandle>(null)

  // Warn before discarding unsaved edits.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
      }
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [hasUnsavedChanges])

  // Persist the draft to localStorage on every change.
  const draftKey = dtaId ? `cdr-draft-${dtaId}` : null
  const saveDraft = useCallback(() => {
    if (!draftKey || !editorRef.current) return
    const content = editorRef.current.getContent()
    localStorage.setItem(draftKey, JSON.stringify(content))
  }, [draftKey])

  useEffect(() => {
    if (hasUnsavedChanges) saveDraft()
  }, [hasUnsavedChanges, saveDraft])

  useEffect(() => {
    if (!draftKey) return
    const saved = localStorage.getItem(draftKey)
    if (saved) {
      try {
        setRecoveredDraft(JSON.parse(saved))
      } catch {
        /* ignore corrupt draft */
      }
    }
  }, [draftKey])

  const {
    stream,
    setStream,
    error,
    versions,
    setVersions,
    selectedVersion,
    setSelectedVersion,
    viewedStage,
    setViewedStage,
    versionLoading,
    setVersionLoading,
    forbiddenVersion,
    refetch,
  } = useDataStreamDetails(dtaId, urlVersion)

  const queryClient = useQueryClient()
  const updateDta = useUpdateDta(dtaId ?? "")
  const uploadDocument = useUploadDocument(stream?.study_id ?? "")
  const uploadAndParseEdc = useUploadAndParseEdc(stream?.study_id ?? "")
  const uploadAndParseSdtm = useUploadAndParseSdtm(stream?.study_id ?? "")

  // Redirect to the versionless URL when the pinned version is no longer accessible.
  useEffect(() => {
    if (!forbiddenVersion || !dtaId) return
    showToast({
      variant: "default",
      subject: "This version isn't available",
      message: "Showing the latest version.",
    })
    navigate(`/dtas/${dtaId}`, { replace: true })
  }, [forbiddenVersion, dtaId, navigate, showToast])

  useEffect(() => {
    if (stream?.extraction_status !== "IN_PROGRESS") return
    const interval = setInterval(() => {
      void refetch()
    }, 5000)
    return () => clearInterval(interval)
  }, [stream?.extraction_status, refetch])

  // Toast once when extraction moves to FAILED; the editor tab also shows an error state.
  const prevExtractionStatusRef = useRef<DTADetails["extraction_status"]>(undefined)
  useEffect(() => {
    const status = stream?.extraction_status
    if (status === "FAILED" && prevExtractionStatusRef.current !== "FAILED") {
      showToast({
        subject: "Content extraction failed",
        message: "You can edit the DTA manually or reupload the source document.",
        variant: "error",
      })
    }
    prevExtractionStatusRef.current = status
  }, [stream?.extraction_status, showToast])

  const signing = useSigningWorkflow(dtaId, () => void refetch())

  const amendment = useAmendment(dtaId, versions, (newVersion: DTAVersion) => {
    setVersions((prev) => [newVersion, ...prev])
    setStream((prev) =>
      prev ? { ...prev, workflow_stage: "DRAFT", version: newVersion.version } : prev,
    )
    setSelectedVersion(newVersion.version)
    window.history.replaceState(null, "", `/dtas/${dtaId}/v/${newVersion.version}`)
  })

  const inlineDiff = useInlineDiff(
    dtaId,
    selectedVersion || stream?.version || "",
    versions,
    stream?.document_content,
  )

  const displayStage = viewedStage ?? stream?.workflow_stage

  // Stage guidance, shown as helper text in the header.
  const userApproved =
    stream?.approvals?.some((a) => a.approver_id === user?.email && a.status === "APPROVED") ??
    false
  const guidance = stageGuidanceText(displayStage, isDAE, isVendor, userApproved)

  // Clear the editor sub-tab param when navigating away from the editor tab.
  const handleTabChange = (activeTab: string, params: URLSearchParams) => {
    if (activeTab !== "editor") {
      params.delete("editorTab")
    }
  }

  const handleVersionChange = (ver: string) => {
    if (!dtaId) return
    setSelectedVersion(ver)
    setVersionLoading(true)
    navigate(`/dtas/${dtaId}/v/${ver}`, { replace: true })
    void refetch()
    const isLatest = versions.length > 0 && ver === versions[0].version
    const selectedVer = versions.find((v) => v.version === ver)
    if (isLatest) {
      setViewedStage(selectedVer?.workflow_stage || null)
      setVersionLoading(false)
    } else {
      void queryClient
        .fetchQuery({
          queryKey: dtaKeys.version(dtaId, ver),
          queryFn: () => dtaApiService.getVersion(dtaId, ver),
        })
        .then((snapshot) => {
          setViewedStage(snapshot.workflow_stage || null)
        })
        .catch(() => {})
        .finally(() => setVersionLoading(false))
    }
  }

  const canEdit = () => {
    if (!user || !stream) return false
    return canEditDta(stream, !!isDAE)
  }

  const handleUploadSourceDocument = async (file: File) => {
    if (!dtaId || !stream) return
    const isSpecDerived = isSpecDerivedSource(stream.source_type)

    if (isSpecDerived) {
      setEdcReuploadForms([])
      setEdcReuploadLoading(true)
      try {
        const parsed =
          stream.source_type === "sdtm"
            ? await uploadAndParseSdtm.mutateAsync({ file })
            : await uploadAndParseEdc.mutateAsync({ file })
        if (parsed.forms?.length) {
          setEdcReuploadSelected(
            selectReuploadForm(parsed.forms, stream.domains?.[0]) ?? parsed.forms[0].name,
          )
          setEdcReuploadForms(parsed.forms)
        } else {
          setEdcReuploadForms(null)
          showToast({
            subject: "No forms found",
            message: "The re-uploaded file did not contain any recognizable domains.",
            variant: "error",
          })
        }
      } catch (err: unknown) {
        setEdcReuploadForms(null)
        showToast({
          subject: "Failed to parse the re-uploaded file",
          message: err instanceof Error ? err.message : String(err),
          variant: "error",
        })
      } finally {
        setEdcReuploadLoading(false)
      }
    } else {
      document.body.style.cursor = "wait"
      try {
        const { upload_id } = await uploadDocument.mutateAsync({ file, uploadType: "dta" })
        await updateDta.mutateAsync({ upload_id })
        // Reflect the extraction kicking off immediately; polling then tracks the real status.
        setStream((prev) =>
          prev
            ? { ...prev, extraction_status: "IN_PROGRESS" as DTADetails["extraction_status"] }
            : prev,
        )
      } finally {
        document.body.style.cursor = ""
      }
    }
  }

  if (!dtaId)
    return (
      <div className="dta-page">
        <div className="dta-error">DTA ID Required</div>
      </div>
    )
  if (error)
    return (
      <div className="dta-page">
        <div className="dta-error">{error}</div>
      </div>
    )

  if (!stream)
    return (
      <div className="dta-page">
        <Breadcrumb
          items={
            isVendor
              ? [{ label: "My DTAs", path: "/vendor/dashboard" }, { label: "Loading..." }]
              : [
                  { label: "Studies", path: "/studies" },
                  { label: "..." },
                  { label: "Data Transfer Agreements" },
                  { label: "Loading..." },
                ]
          }
        />
        <DataStreamSkeleton />
      </div>
    )

  // Internal callers comment as "DAE", external callers as the DTA's vendor.
  const commentVendorCompany = isVendor ? stream.vendor_name || "Vendor" : "DAE"
  const commentVersion = selectedVersion || stream.version
  // Let comment navigation switch the page tab via `?tab=`.
  const switchPageTab = (tabId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("tab", tabId)
      return next
    })
  }

  return (
    <DtaCommentsProvider
      dtaId={dtaId}
      vendorCompany={commentVendorCompany}
      version={commentVersion}
    >
      <PageTabBridge onSwitchTab={switchPageTab} />
      <div className="dta-page">
        <Breadcrumb
          items={
            isVendor
              ? [
                  { label: "My DTAs", path: "/vendor/dashboard" },
                  { label: getDTADisplayName(stream) },
                ]
              : [
                  { label: "Studies", path: "/studies" },
                  { label: stream.study_id, path: `/studies/${stream.study_id}` },
                  { label: "DTAs", path: `/studies/${stream.study_id}?tab=dtas` },
                  { label: getDTADisplayName(stream) },
                ]
          }
        />

        {/* Errors from a failed Submit for Review, the only message shown at the page top. */}
        <ValidationErrorsAlert
          errors={validationErrors}
          onDismiss={() => setValidationErrors([])}
          onErrorClick={(tab, field) => {
            setTimeout(() => editorRef.current?.navigateToTab(tab, field), 100)
          }}
        />

        {/* Tabs sit inside the header card so tab-row actions align with the header. */}
        <DataStreamHeader
          stream={stream}
          dtaId={dtaId}
          versions={versions}
          selectedVersion={selectedVersion}
          displayStage={displayStage}
          versionLoading={versionLoading}
          isDAE={isDAE}
          guidance={guidance}
          extractionStatus={stream.extraction_status}
          onVersionChange={handleVersionChange}
          actions={
            <DataStreamActions
              stream={stream}
              dtaId={dtaId}
              versions={versions}
              selectedVersion={selectedVersion || stream.version}
              displayStage={displayStage}
              isDAE={isDAE}
              isVendor={isVendor}
              filings={signing.filings}
              onAmendClick={amendment.openModal}
              onSignClick={signing.openModal}
              onFileClick={() => void signing.fetchSigningAndFilings()}
              onRefresh={() => void refetch()}
              onWorkflowError={(msg) => {
                if (msg) {
                  showToast({ subject: "Action failed", message: msg, variant: "error" })
                }
              }}
              validateBeforePublish={() => {
                // The mounted editor (Details tab) runs the full publish checks. From the
                // Overview tab it is unmounted (editorRef null, #1241), so fall back to the
                // shared date-format readiness check over the persisted/recovered content.
                const errors =
                  editorRef.current?.validateForPublish() ??
                  validateDateFormats(
                    (recoveredDraft as DTADocumentContent) ?? stream.document_content,
                  )
                if (errors.length > 0) {
                  setValidationErrors(errors)
                  setSearchParams((prev) => {
                    prev.set("tab", "editor")
                    return prev
                  })
                }
                return errors
              }}
            />
          }
          tabs={
            <div className="dta-tabs-band">
              <SharedTabs
                ariaLabel="DTA sections"
                scrollToTopOnChange
                onTabChange={handleTabChange}
                actions={
                  <DataStreamContentActions
                    canEdit={canEdit()}
                    hasUnsavedChanges={hasUnsavedChanges}
                    isSaving={savingDocument || updateDta.isPending}
                    onSave={() => void editorRef.current?.triggerSave()}
                    onUploadSourceDocument={
                      stream.workflow_stage === "DRAFT" &&
                      stream.extraction_status !== "IN_PROGRESS"
                        ? handleUploadSourceDocument
                        : undefined
                    }
                  />
                }
                tabs={[
                  {
                    id: "overview",
                    label: DTA_TAB_LABELS["overview"],
                    content: (
                      <OverviewPanel
                        stream={stream}
                        dtaId={dtaId}
                        selectedVersion={selectedVersion || stream.version}
                        displayStage={displayStage}
                        signingRecords={signing.signingRecords}
                        filings={signing.filings}
                        onRefresh={() => void refetch()}
                        onViewAuditTrail={() => switchPageTab("audit-trail")}
                      />
                    ),
                  },
                  {
                    id: "editor",
                    label: DTA_TAB_LABELS["editor"],
                    content: (
                      <div className="dta-editor-container">
                        <EditorTabMessages
                          extractionStatus={stream.extraction_status}
                          hasRecoveredDraft={recoveredDraft !== null}
                          onDiscardDraft={() => {
                            setRecoveredDraft(null)
                            if (draftKey) localStorage.removeItem(draftKey)
                          }}
                          onReupload={
                            stream.workflow_stage === "DRAFT"
                              ? handleUploadSourceDocument
                              : undefined
                          }
                        />
                        {documentEditError && <Alert type="error">{documentEditError}</Alert>}
                        <DTAEditor
                          key={stream.last_modified_at}
                          ref={editorRef}
                          dta={
                            recoveredDraft
                              ? ({ ...stream, document_content: recoveredDraft } as DTADetails)
                              : stream
                          }
                          canEdit={canEdit()}
                          tableNameLocked={
                            hasTableName(recoveredDraft ?? stream.document_content) &&
                            versions.some(
                              (version) =>
                                ["APPROVED", "ACTIVE", "INACTIVE"].includes(
                                  version.workflow_stage || "",
                                ) || !!(version as { approved_at?: string | null }).approved_at,
                            )
                          }
                          isSaving={savingDocument || updateDta.isPending}
                          onHasChangesChange={setHasUnsavedChanges}
                          inlineDiff={inlineDiff}
                          initialTab={searchParams.get("editorTab") || undefined}
                          onTabChange={(tabId) => {
                            setSearchParams((prev) => {
                              prev.set("editorTab", tabId)
                              return prev
                            })
                          }}
                          onSave={async (content) => {
                            if (!dtaId) return
                            try {
                              setDocumentEditError(null)
                              await updateDta.mutateAsync({ document_content: content })
                              // Clear the draft so the remounted editor seeds from
                              // the saved content, not the stale draft.
                              setRecoveredDraft(null)
                              if (draftKey) localStorage.removeItem(draftKey)
                            } catch (err) {
                              const message = parseApiErrorMessage(err, "Save failed")
                              if (!isTableNameSaveError(message)) {
                                setDocumentEditError(message)
                              }
                              throw new Error(message)
                            }
                          }}
                        />
                      </div>
                    ),
                  },
                  {
                    id: "collaboration",
                    label: DTA_TAB_LABELS["collaboration"],
                    content: (
                      <CollaborationPanel
                        stream={stream}
                        dtaId={dtaId}
                        isDAE={!!isDAE}
                        isVendor={!!isVendor}
                        version={selectedVersion || stream.version}
                      />
                    ),
                  },
                  {
                    id: "audit-trail",
                    label: DTA_TAB_LABELS["audit-trail"],
                    content: (
                      <AuditTrailPanel
                        statusHistory={stream.status_history}
                        versions={versions}
                        selectedVersion={selectedVersion || stream.version}
                        onVersionChange={handleVersionChange}
                      />
                    ),
                  },
                ]}
              />
            </div>
          }
        />

        <SignModal
          open={signing.isModalOpen}
          onClose={signing.closeModal}
          comment={signing.comment}
          onCommentChange={signing.setComment}
          onSubmit={() => void signing.handleSign()}
          submitting={signing.submitting}
          error={signing.error}
        />
        <AmendModal
          open={amendment.isModalOpen}
          onClose={amendment.closeModal}
          reason={amendment.reason}
          onReasonChange={amendment.setReason}
          onSubmit={() => void amendment.handleAmend()}
          loading={amendment.loading}
          error={amendment.error}
          studyId={stream.study_id}
          sourceType={stream.source_type}
          currentDomain={stream.domains?.[0]}
          onUploadIdChange={amendment.setUploadId}
        />

        {edcReuploadForms !== null && (
          <Modal
            visible
            onDismiss={() => !edcReuploadLoading && !savingDocument && setEdcReuploadForms(null)}
            header={edcReuploadLoading ? "Analyzing EDC Specification" : "Select Domain"}
            size="medium"
          >
            <div style={{ maxHeight: "calc(80vh - 150px)", overflowY: "auto" }}>
              {edcReuploadLoading ? (
                <Box padding={{ vertical: "l" }}>
                  <SpaceBetween size="s" direction="horizontal" alignItems="center">
                    <Spinner size="normal" />
                    <span>Uploading and parsing file...</span>
                  </SpaceBetween>
                  <Box variant="p" color="text-body-secondary" margin={{ top: "s" }}>
                    This will replace all existing content in this data stream.
                  </Box>
                </Box>
              ) : (
                <DomainSelectionStep
                  parseResult={{ forms: edcReuploadForms }}
                  selectedForms={new Set(edcReuploadSelected ? [edcReuploadSelected] : [])}
                  onToggleForm={(name) => setEdcReuploadSelected(name)}
                  onToggleAll={() => {}}
                  onSubmit={() =>
                    void handleEdcReuploadReplace({
                      forms: edcReuploadForms,
                      selectedName: edcReuploadSelected,
                      dtaId,
                      updateContent: updateDta.mutateAsync,
                      setSaving: setSavingDocument,
                      clearForms: () => setEdcReuploadForms(null),
                      showToast,
                    })
                  }
                  onClose={() => setEdcReuploadForms(null)}
                  mode="single"
                  submitLabel="Replace Content"
                  loading={savingDocument}
                  currentDomain={stream.domains?.[0]}
                />
              )}
            </div>
          </Modal>
        )}

        <CommentsRail dtaId={dtaId} vendorCompany={commentVendorCompany} version={commentVersion} />
      </div>
    </DtaCommentsProvider>
  )
}

export default DataStreamDetails
